/**
 * A deliberately small emoji picker (doc 01 comms). A fixed, curated set of
 * Unicode emoji — no external font, no image sprites, no provider call. Picking
 * one hands the raw Unicode back to the caller, which either inserts it into the
 * composer or sends it as a reaction. The server stores the Unicode sequence
 * verbatim, so a newer emoji than the server knows about still works.
 */

/** A compact, workplace-appropriate set. Raw Unicode, grouped loosely by tone. */
export const COMMON_EMOJI = [
  '👍',
  '🙏',
  '👏',
  '🎉',
  '✅',
  '💪',
  '🔥',
  '⭐',
  '😀',
  '😅',
  '😂',
  '🙂',
  '😉',
  '😊',
  '😍',
  '🤩',
  '🤔',
  '👀',
  '🙌',
  '🤝',
  '💯',
  '❤️',
  '🧡',
  '💛',
  '😮',
  '😢',
  '😴',
  '☕',
  '📌',
  '⏰',
  '🚗',
  '📝',
] as const;

import { useFocusTrap } from '@cwos/ui-admin';

export interface EmojiPickerProps {
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Accessible label for the grid (differs for insert vs react). */
  label?: string;
}

export function EmojiPicker({ onPick, onClose, label }: EmojiPickerProps) {
  // Rendered only while open. Focus enters the grid, Escape closes it, and
  // focus goes back to the button in the composer that opened it — none of
  // which happened before, so keyboard users could open this and not get out.
  const ref = useFocusTrap<HTMLDivElement>(true, { onEscape: onClose });

  return (
    <div ref={ref} className="msg-emoji-pop" role="dialog" aria-label={label ?? 'Choose an emoji'}>
      <div className="msg-emoji-grid">
        {COMMON_EMOJI.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="msg-emoji-btn"
            aria-label={emoji}
            onClick={() => {
              onPick(emoji);
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
      <button type="button" className="rn-link msg-emoji-close" onClick={onClose}>
        Close
      </button>
    </div>
  );
}
